import { useState, useCallback, useEffect, useRef } from "react";
import useEmbedMode from "./useEmbedMode";

/**
 * Hook for detecting hover states and capturing underlying URLs in embed mode
 * This enables interactive embedding where users can hover over elements to see
 * what they would navigate to, then click to open in a new window
 */
const useEmbedHoverDetection = () => {
  const { isEmbedMode } = useEmbedMode();
  const [hoveredUrl, setHoveredUrl] = useState(null);
  const [hoveredElement, setHoveredElement] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: null, y: null });
  const hoverTimeoutRef = useRef(null);
  const highlightedElementRef = useRef(null);

  // Function to extract URL from an element
  const extractUrlFromElement = useCallback((element) => {
    if (!element || typeof element.getAttribute !== "function") {
      return null;
    }

    try {
      // Check for href attribute
      const href = element.getAttribute("href");
      if (href) {
        return href;
      }

      // Check for data-href attribute
      const dataHref = element.getAttribute("data-href");
      if (dataHref) {
        return dataHref;
      }

      // Check for to attribute (React Router links)
      const to = element.getAttribute("to");
      if (to) {
        return to;
      }

      // Check for data-to attribute (React Router links)
      const dataTo = element.getAttribute("data-to");
      if (dataTo) {
        return dataTo;
      }

      // Check for data-url attribute
      const dataUrl = element.getAttribute("data-url");
      if (dataUrl) {
        return dataUrl;
      }

      // Check if element is inside a link
      if (typeof element.closest === "function") {
        const parentLink = element.closest("a");
        if (parentLink) {
          return extractUrlFromElement(parentLink);
        }

        // Check if element is inside a button that might be a link
        const parentButton = element.closest("button");
        if (parentButton) {
          const buttonHref =
            parentButton.getAttribute("data-href") ||
            parentButton.getAttribute("data-to") ||
            parentButton.getAttribute("data-url");
          if (buttonHref) {
            return buttonHref;
          }
        }

        // Check for React Router Link components (they often have specific classes)
        const reactLink = element.closest(
          '[class*="link"], [class*="Link"], [class*="router"]'
        );
        if (reactLink) {
          return extractUrlFromElement(reactLink);
        }
      }
    } catch (error) {
      // Silently handle any errors when accessing element properties
    }

    return null;
  }, []);

  const findHoverableElement = useCallback((target) => {
    if (!target || typeof target.closest !== "function") {
      return null;
    }

    if (target.closest("[data-embed-overlay]")) {
      return null;
    }

    const link = target.closest("a[href]");
    if (link) {
      return link;
    }

    const button = target.closest(
      "button[data-href], button[data-to], button[data-url]"
    );
    if (button) {
      return button;
    }

    const reactLink = target.closest(
      '[class*="link"], [class*="Link"], [class*="router"]'
    );
    if (reactLink) {
      return reactLink;
    }

    return null;
  }, []);

  // Highlight the navigable container only (never inner tag/badge nodes).
  // Use outline instead of transform so nested flex layouts do not shift.
  const addHoverVisualFeedback = useCallback(
    (element) => {
      if (!element || !isEmbedMode) {
        return;
      }

      try {
        element.style.cursor = "pointer";
        element.style.outline = "2px solid rgba(0, 102, 204, 0.45)";
        element.style.outlineOffset = "2px";
      } catch (error) {
        // Silently handle errors in visual feedback
      }
    },
    [isEmbedMode]
  );

  const removeHoverVisualFeedback = useCallback(
    (element) => {
      if (!element || !isEmbedMode) {
        return;
      }

      try {
        element.style.cursor = "";
        element.style.outline = "";
        element.style.outlineOffset = "";
      } catch (error) {
        // Silently handle errors in visual feedback removal
      }
    },
    [isEmbedMode]
  );

  const clearHighlightedElement = useCallback(() => {
    if (highlightedElementRef.current) {
      removeHoverVisualFeedback(highlightedElementRef.current);
      highlightedElementRef.current = null;
    }
  }, [removeHoverVisualFeedback]);

  const handleMouseOver = useCallback(
    (event) => {
      if (!isEmbedMode) {
        return;
      }

      try {
        const hoverable = findHoverableElement(event.target);
        if (!hoverable) {
          return;
        }

        const url = extractUrlFromElement(hoverable);
        if (!url) {
          return;
        }

        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }

        hoverTimeoutRef.current = setTimeout(() => {
          if (
            highlightedElementRef.current &&
            highlightedElementRef.current !== hoverable
          ) {
            removeHoverVisualFeedback(highlightedElementRef.current);
          }

          highlightedElementRef.current = hoverable;
          setHoveredUrl(url);
          setHoveredElement(hoverable);
          setMousePosition({ x: event.clientX, y: event.clientY });
          addHoverVisualFeedback(hoverable);
        }, 200);
      } catch (error) {
        // Silently handle any errors in mouse event handling
      }
    },
    [
      isEmbedMode,
      findHoverableElement,
      extractUrlFromElement,
      addHoverVisualFeedback,
      removeHoverVisualFeedback,
    ]
  );

  const handleMouseOut = useCallback(
    (event) => {
      if (!isEmbedMode) {
        return;
      }

      try {
        const hoverable = findHoverableElement(event.target);
        if (!hoverable) {
          return;
        }

        const relatedTarget = event.relatedTarget;
        if (
          relatedTarget &&
          typeof hoverable.contains === "function" &&
          hoverable.contains(relatedTarget)
        ) {
          return;
        }

        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }

        hoverTimeoutRef.current = setTimeout(() => {
          if (highlightedElementRef.current === hoverable) {
            clearHighlightedElement();
          }
          setHoveredUrl(null);
          setHoveredElement(null);
          setMousePosition({ x: null, y: null });
        }, 100);
      } catch (error) {
        // Silently handle any errors in mouse event handling
      }
    },
    [isEmbedMode, findHoverableElement, clearHighlightedElement]
  );

  // Function to get the URL that should be opened when clicking
  const getClickUrl = useCallback(() => {
    return hoveredUrl || window.location.href;
  }, [hoveredUrl]);

  // Function to handle click events in embed mode
  const handleEmbedClick = useCallback(
    (event) => {
      if (!isEmbedMode) {
        return;
      }

      const url = getClickUrl();
      if (url && url !== window.location.href) {
        event.preventDefault();
        event.stopPropagation();

        import("src/util/embedUtils").then(({ getCleanUrlForNewWindow }) => {
          const cleanUrl = getCleanUrlForNewWindow(url);
          window.open(cleanUrl, "_blank", "noopener,noreferrer");
        });
      }
    },
    [isEmbedMode, getClickUrl]
  );

  useEffect(() => {
    if (!isEmbedMode) {
      clearHighlightedElement();
      setHoveredUrl(null);
      setHoveredElement(null);
      return;
    }

    const safeMouseOver = (event) => {
      try {
        handleMouseOver(event);
      } catch (error) {
        // Silently handle mouse over errors
      }
    };

    const safeMouseOut = (event) => {
      try {
        handleMouseOut(event);
      } catch (error) {
        // Silently handle mouse out errors
      }
    };

    document.addEventListener("mouseover", safeMouseOver, true);
    document.addEventListener("mouseout", safeMouseOut, true);

    return () => {
      document.removeEventListener("mouseover", safeMouseOver, true);
      document.removeEventListener("mouseout", safeMouseOut, true);

      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      clearHighlightedElement();
    };
  }, [isEmbedMode, handleMouseOver, handleMouseOut, clearHighlightedElement]);

  return {
    hoveredUrl,
    hoveredElement,
    isHovering: !!hoveredUrl,
    mousePosition,
    handleEmbedClick,
    getClickUrl,
  };
};

export default useEmbedHoverDetection;
